import React, { useState } from 'react';
import { Alert, App, Button, Form, Input } from 'antd';

import { useAccess } from '../../auth/AccessContext.jsx';
import PasswordField from '../../auth/PasswordField.jsx';
import ActionIcon from '../../components/ActionIcon.jsx';
import NotificationPreferences from '../../notifications/NotificationPreferences.jsx';

const at = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { timeZone: 'Asia/Colombo', dateStyle: 'medium', timeStyle: 'short' }) : '—');

/**
 * The signed-in person's own profile (docs/auth/console-identity-and-access.md
 * section 9): their password, and what the Console knows about them. The email
 * is the account, and the names are set by the administrator who invited them,
 * so neither can be changed here; roles are an administrator's to change too.
 */
const ProfilePage = () => {
    const { api, principal } = useAccess();
    const { message } = App.useApp();
    const [passwordForm] = Form.useForm();
    const [changing, setChanging] = useState(false);
    const [passwordError, setPasswordError] = useState(null);

    const changePassword = async ({ currentPassword, newPassword }) => {
        setChanging(true);
        setPasswordError(null);
        try {
            const { otherSessionsEnded } = await api.profile.changePassword(currentPassword, newPassword);
            passwordForm.resetFields();
            message.success('Your password is changed.' + (otherSessionsEnded ? ' Your other sessions were signed out.' : ''));
        } catch (e) {
            const issues = e.details?.issues;
            setPasswordError(issues?.length ? issues.map((i) => i.message).join(' ') : e.message);
        } finally {
            setChanging(false);
        }
    };

    const person = { email: principal.email, firstName: principal.firstName, lastName: principal.lastName };
    const authenticator = principal.credential?.state === 'ACTIVE'
        ? <>Set up {at(principal.credential.activatedAt)}. To move it to a new phone, ask an administrator to reset it.</>
        : principal.credential?.state ?? 'Not set up';

    return (
        <div className="workspace profile-page">
            <div className="workspace-body">
                <div className="profile-top-grid">
                    <div className="profile-left-column">
                        <div className="panel profile-account-panel">
                            <h3 className="panel-title">Your Account</h3>
                            <div className="profile-account-fields">
                                <div className="profile-account-field">
                                    <span className="profile-field-label">Email</span>
                                    <div className="profile-readonly-value">{principal.email}</div>
                                </div>
                                <div className="profile-account-field">
                                    <span className="profile-field-label">Roles</span>
                                    <div className="profile-readonly-value">{principal.roles.length ? principal.roles.join(', ') : 'None'}</div>
                                </div>
                                <div className="profile-account-field">
                                    <span className="profile-field-label">Authenticator</span>
                                    <div className="profile-readonly-value">{authenticator}</div>
                                </div>
                            </div>
                            <p className="profile-session">
                                This session ends by  {at(principal.session?.absoluteExpiresAt)}, or after 10 minutes of inactivity,whichever comes first.
                            </p>
                        </div>

                        <div className="panel profile-name-panel">
                            <h3 className="panel-title">Your Name</h3>
                            {/* Read-only: accounts are created by an administrator, who sets the names. */}
                            <div className="profile-account-fields">
                                <div className="profile-account-field">
                                    <span className="profile-field-label">First Name</span>
                                    <div className="profile-readonly-value">{principal.firstName}</div>
                                </div>
                                <div className="profile-account-field">
                                    <span className="profile-field-label">Last Name</span>
                                    <div className="profile-readonly-value">{principal.lastName}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="panel profile-password-panel">
                        <h3 className="panel-title">Change your password</h3>
                        <div className="permission-info-banner profile-info-banner">
                            <ActionIcon name="info" />
                            <span>Every other session you have open is signed out when your password changes.</span>
                        </div>
                        {passwordError && <Alert type="error" showIcon message={passwordError} className="auth-alert" />}
                        <Form className="profile-password-form" form={passwordForm} layout="vertical" requiredMark={false}
                            onFinish={changePassword} disabled={changing}>
                            <Form.Item name="currentPassword" label="Current password" rules={[{ required: true, message: 'Enter your current password.' }]}>
                                <Input.Password size="large" autoComplete="current-password" />
                            </Form.Item>
                            <PasswordField person={person} currentField="currentPassword" />
                            <Button type="primary" htmlType="submit" loading={changing}>Change password</Button>
                        </Form>
                    </div>
                </div>

                {/* What the person is told about, and their mutes (docs/notifications/implementation-guide.md 8.2). */}
                <NotificationPreferences />
            </div>
        </div>
    );
};

export default ProfilePage;
